import { useMemo } from 'react'

import platform1 from 'src/Assets/Webp/TikTokErrorPlatform1.webp'
import platform2 from 'src/Assets/Webp/TikTokErrorPlatform2.webp'
import link2 from 'src/Assets/Webp/TikTokErrorLink2.webp'
import link1 from 'src/Assets/Webp/TikTokErrorLink1.webp'
import original2 from 'src/Assets/Webp/TikTokErrorOriginal2.webp'
import original1 from 'src/Assets/Webp/TikTokErrorOriginal1.webp'
import { TikTokTrackSearchErrorEnum } from 'src/Redux/tiktokTrack-process/utils'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
} from 'src/Types/campaigns'
import { CampaignListItem } from 'src/Components/CampaignListItem'
import { ExistingTikTokCampaign } from 'src/Redux/tiktokTrack-process'

import { TrackSearchError } from '../TrackSearchError'

type ErrorImages = { title: string; images: string[] } | string

interface UseTikTokTrackErrorDataParams {
  error: TikTokTrackSearchErrorEnum | string
  status?: CampaignStatusEnum
  isFetching?: boolean
  campaign?: ExistingTikTokCampaign | null
  onStartCampaignClick: () => void
  onDetailsClick: () => void
}

export const useTikTokTrackErrorData = ({
  error,
  status,
  isFetching,
  campaign,
  onStartCampaignClick,
  onDetailsClick,
}: UseTikTokTrackErrorDataParams): {
  visualError: ErrorImages | null
  campaignErrorElement: JSX.Element | null
} =>
  useMemo(() => {
    if (!error || isFetching) {
      return { visualError: null, campaignErrorElement: null }
    }

    const isExistingCampaign =
      error === TikTokTrackSearchErrorEnum.ExistingCampaignTrack

    if (isExistingCampaign) {
      const campaignElement = getExistingCampaignElement(
        status,
        campaign,
        onStartCampaignClick,
        onDetailsClick,
      )
      return {
        visualError: null,
        campaignErrorElement: campaignElement,
      }
    }

    return {
      visualError: getVisualError(error),
      campaignErrorElement: null,
    }
  }, [
    error,
    status,
    isFetching,
    campaign,
    onStartCampaignClick,
    onDetailsClick,
  ])

const getExistingCampaignElement = (
  status?: CampaignStatusEnum,
  campaign?: ExistingTikTokCampaign | null,
  onStartCampaignClick?: () => void,
  onDetailsClick?: () => void,
): JSX.Element | null => {
  if (
    status === CampaignStatusEnum.PENDING ||
    status === CampaignStatusEnum.AWAITING_REVIEW
  ) {
    return (
      <CampaignListItem
        status={status}
        platform={CampaignPlatformTypeEnum.TIKTOK}
        trackName={campaign?.trackTitle}
        artistName={campaign?.trackAuthorName}
        trackImage={campaign?.trackCoverUrl}
        onStartCampaignClick={onStartCampaignClick}
        onDetailsClick={onDetailsClick}
      />
    )
  }

  if (
    status === CampaignStatusEnum.SCHEDULED ||
    status === CampaignStatusEnum.RUNNING
  ) {
    return (
      <TrackSearchError>
        You&apos;ve already started a campaign for this track. To continue,
        either select a new track or check the campaign&apos;s details.
      </TrackSearchError>
    )
  }

  return null
}

const getVisualError = (
  error: TikTokTrackSearchErrorEnum | string,
): ErrorImages | null => {
  switch (error) {
    case TikTokTrackSearchErrorEnum.NotOfficialTrackError:
      return {
        title: "The link you provided isn't the official one for this track.",
        images: [original1, original2],
      }

    case TikTokTrackSearchErrorEnum.UserLinkError:
      return {
        title: 'It looks like this is a TikTok user link.',
        images: [link1, link2],
      }

    case TikTokTrackSearchErrorEnum.WrongPlatformError:
      return {
        title: "You've added a link from a different service.",
        images: [platform1, platform2],
      }

    case TikTokTrackSearchErrorEnum.WrongFirstCharacter:
      return 'Oops! It seems like you entered @nickname. To find the right track, just enter the track name into the search bar.'

    case TikTokTrackSearchErrorEnum.DefaultError:
      return 'Oops! Something went wrong. Please try again later.'

    default:
      return typeof error === 'string' ? error : null
  }
}
